import {
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  OnDestroy,
} from "@angular/core";
import { toSignal, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzToolTipModule } from "ng-zorro-antd/tooltip";
import { map } from "rxjs/operators";

import { JobStateService } from "../job-state.store";
import { CopilotContextService } from "../../../../shared/services/copilot-context.service";
import { CachedUserService } from "../../../../shared/services/cached-user.service";
import { CopilotModule } from "../../../../copilot/copilot.module";
import { XwaiV2StreamService } from "../../../../xwai-v2/stream/xwai-v2-stream.service";
import { XwaiV2ShellComponent } from "../../../../xwai-v2/shell/xwai-v2-shell.component";

type AiTab = "copilot" | "xwai-v2";

/**
 * Collapsible XWAI panel embedded in the model viewer, combining the legacy
 * copilot and the newer xwai-v2 chat behind one floating shell with a tab
 * switcher. Both chats stay mounted after first open (just hidden, not
 * destroyed) so switching tabs never loses an in-flight stream or history.
 */
@Component({
  selector: "app-model-ai-panel",
  standalone: true,
  imports: [
    CommonModule,
    NzIconModule,
    NzToolTipModule,
    CopilotModule,
    XwaiV2ShellComponent,
  ],
  templateUrl: "./model-ai-panel.component.html",
  styleUrls: ["./model-ai-panel.component.less"],
})
export class ModelAiPanelComponent implements OnDestroy {
  private jobState = inject(JobStateService);
  private copilotContext = inject(CopilotContextService);
  private xwaiV2Stream = inject(XwaiV2StreamService);
  private userService = inject(CachedUserService);
  private destroyRef = inject(DestroyRef);

  open = signal<boolean>(false);
  expanded = signal<boolean>(true);
  activeTab = signal<AiTab>("xwai-v2");

  // True when an answer completed while the panel was closed; surfaced as a dot
  // on the toggle button and cleared the next time the panel is opened.
  hasUnread = signal<boolean>(false);

  // Once the panel has been opened we keep both chats mounted (the panel just
  // slides off-screen / hides when closed or when the other tab is active).
  // This preserves in-flight streams, message history and thread_ids across
  // close/reopen and tab switches, so an answer that arrives on one tab while
  // the other is showing is still there when the user switches back.
  hasBeenOpened = signal<boolean>(false);

  // XWAI is staff-only (is_staff || general@seimax.com). The panel is
  // self-protecting so it stays gated wherever it is embedded, independent of
  // the host template. Mirrors the /xwai route guard's `xwaiAccess` check.
  hasAccess = toSignal(
    this.userService.getUserDetails().pipe(map((d) => !!d?.xwaiAccess)),
    { initialValue: false }
  );

  // v1 (single-agent) is staff-only on the backend: its tools aren't scoped
  // per user, and the 403s it would get otherwise log the user out.
  isStaff = toSignal(
    this.userService.getUserDetails().pipe(map((d) => !!d?.isStaff)),
    { initialValue: false }
  );

  constructor() {
    // Mirror the current job/iteration/model into both chats' context so
    // "this job"/"this iteration" resolve, and refresh the copilot's quick
    // prompts.
    effect(() => {
      if (!this.hasAccess()) {
        this.copilotContext.clearContext();
        this.xwaiV2Stream.setJobId(null);
        return;
      }
      const job = this.jobState.jobData();
      if (!job?.id) {
        this.copilotContext.clearContext();
        this.xwaiV2Stream.setJobId(null);
        return;
      }
      this.xwaiV2Stream.setJobId(job.id);
      const iteration = this.jobState.iteration();
      const model = this.jobState.selectedModel();

      this.copilotContext.setContext({
        jobId: job.id,
        jobName: job.name,
        projectId: job.projectId,
        projectName: job.projectName,
        iteration,
        model,
      });

      this.copilotContext.setQuickPrompts([
        {
          label: "How did it converge?",
          prompt:
            "How did this job converge? Plot the trace fit over iterations and " +
            "summarise the trend per functional block.",
        },
        {
          label: "Compare to parent",
          prompt:
            "Compare this job to its parent job — both the parameter " +
            "differences and the convergence. Be careful to compare " +
            "like-for-like (same functional block, frequency and offsets).",
        },
        {
          label: "Explain the sequence",
          prompt:
            "Explain the functional sequence and frequencies used in this job, " +
            "block by block.",
        },
        {
          label: "Did anything stall?",
          prompt:
            "Looking at this job's convergence, did any functional block stall " +
            "or diverge, and what might explain it?",
        },
      ]);
    });

  }

  toggle() {
    this.open.update((v) => !v);
    if (this.open()) {
      this.hasBeenOpened.set(true);
      this.hasUnread.set(false);
    }
  }

  toggleExpanded() {
    this.expanded.update((v) => !v);
  }

  selectTab(tab: AiTab) {
    this.activeTab.set(tab);
  }

  ngOnDestroy() {
    this.copilotContext.clearContext();
    this.xwaiV2Stream.setJobId(null);
  }
}
